import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import logger from '../utils/logger';
import { resolvePhotoUrl, resolveWorkerPhotoUrl, PLACEHOLDER_IMAGE } from '../utils/media';
import ChatMessage from '../components/ChatMessage';
import ChatCallPanel from '../components/ChatCallPanel';
import { database } from '../firebase';
import { ref, onChildAdded, off } from 'firebase/database';
import { collectPendingDeliveryMessages, getVisibleChatMessageIds } from '../utils/chat';

function Chat() {
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [isRoomJoined, setIsRoomJoined] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const { user } = useAuth();
  const { socket, allMessages, addMessage, removeMessage } = useSocket();
  const { chatId } = useParams();
  const typingTimeoutRef = useRef(null);
  const typingLastSentAtRef = useRef(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const lastRenderedMessageIdRef = useRef(null);
  const historyPollRef = useRef(null);
  const historyCursorRef = useRef(null);
  const olderHistoryCursorRef = useRef(null);
  const initialHistoryLoadedRef = useRef(false);
  const readReceiptInFlightRef = useRef(new Set());
  const pendingDeliveryRef = useRef(new Map());
  const pendingMessageRef = useRef(null);
  const addMessageRef = useRef(addMessage);
  useEffect(() => { addMessageRef.current = addMessage; }, [addMessage]);

  const messages = allMessages[chatId] || [];
  // Resolve the peer from persisted message identities. Splitting room IDs is
  // unsafe because Firebase UIDs may themselves contain hyphens.
  const otherUserUid = messages.find(message => message.senderUid === user?.uid)?.receiverUid ||
    messages.find(message => message.receiverUid === user?.uid)?.senderUid || null;
  const otherProfilePath = otherUser?.uid || otherUserUid ? `/profile/${encodeURIComponent(otherUser?.uid || otherUserUid)}` : '/profile';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage ? String(lastMessage._id) : null;
    if (pendingScrollRestoreRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const previous = pendingScrollRestoreRef.current;
      container.scrollTop = previous.scrollTop + (container.scrollHeight - previous.scrollHeight);
      pendingScrollRestoreRef.current = null;
    } else if (lastMessageId && lastMessageId !== lastRenderedMessageIdRef.current) {
      scrollToBottom();
    }
    lastRenderedMessageIdRef.current = lastMessageId;
  }, [messages]);

  useEffect(() => {
    // MongoDB remains authoritative. Fetch the latest page once, then request
    // only messages newer than the last persisted (timestamp, ObjectId) cursor.
    let cancelled = false;
    let syncInProgress = false;
    historyCursorRef.current = null;
    olderHistoryCursorRef.current = null;
    initialHistoryLoadedRef.current = false;
    pendingDeliveryRef.current.clear();

    const syncHistory = async () => {
      if (cancelled || syncInProgress) return;
      syncInProgress = true;
      try {
        const cursor = historyCursorRef.current;
        const query = cursor
          ? `?limit=100&after=${encodeURIComponent(cursor.timestamp)}&afterId=${encodeURIComponent(cursor.id)}`
          : '?limit=50';
        const result = await apiClient.get(`/chat/history/${encodeURIComponent(chatId)}${query}`);
        if (cancelled || !Array.isArray(result?.messages)) return;

        const fetched = result.messages;
        if (!initialHistoryLoadedRef.current) {
          const oldest = fetched.find(message =>
            message._id && /^[a-fA-F0-9]{24}$/.test(String(message._id))
          );
          olderHistoryCursorRef.current = oldest ? {
            timestamp: new Date(oldest.timestamp).toISOString(),
            id: String(oldest._id),
          } : null;
          setHasOlderMessages(fetched.length === 50 && Boolean(olderHistoryCursorRef.current));
          initialHistoryLoadedRef.current = true;
        }
        fetched.forEach(message => addMessageRef.current(message));

        const persisted = fetched.filter(message =>
          message._id && /^[a-fA-F0-9]{24}$/.test(String(message._id))
        );
        if (persisted.length) {
          const latest = persisted[persisted.length - 1];
          historyCursorRef.current = {
            timestamp: new Date(latest.timestamp).toISOString(),
            id: String(latest._id),
          };
        }

        // Delivery means the recipient's authenticated client has retrieved
        // the persisted message. Read is handled separately by visibility.
        // Keep failed delivery receipts queued independently of the history
        // cursor. A successful history sync must not make a failed receipt
        // disappear from future retry attempts.
        const awaitingDelivery = collectPendingDeliveryMessages(
          fetched,
          pendingDeliveryRef.current,
          user.uid
        );
        for (let index = 0; index < awaitingDelivery.length; index += 100) {
          const batch = awaitingDelivery.slice(index, index + 100);
          try {
            await apiClient.post('/chat/delivered', {
              roomId: chatId,
              messageIds: batch.map(message => String(message._id)),
            });
            batch.forEach(message => pendingDeliveryRef.current.delete(String(message._id)));
            if (!cancelled) batch.forEach(message =>
              addMessageRef.current({ ...message, status: 'delivered' })
            );
          } catch (deliveryError) {
            // Leave the batch in pendingDeliveryRef so the next sync retries it.
            logger.debug('Message delivery receipt deferred:', deliveryError?.status || 'network error');
          }
        }
      } catch (historyError) {
        if (!cancelled) logger.debug('Chat history sync unavailable:', historyError?.status || 'network error');
      } finally {
        syncInProgress = false;
      }
    };

    const pollHistory = () => {
      if (document.visibilityState === 'visible') syncHistory();
    };
    if (user?.uid && chatId) {
      syncHistory();
      historyPollRef.current = window.setInterval(pollHistory, 5000);
      document.addEventListener('visibilitychange', pollHistory);
    }
    return () => {
      cancelled = true;
      if (historyPollRef.current) window.clearInterval(historyPollRef.current);
      document.removeEventListener('visibilitychange', pollHistory);
      historyPollRef.current = null;
    };
  }, [chatId, user?.uid]);

  useEffect(() => {
    // Only mark incoming messages as read after their message row is actually
    // visible in the active, foreground conversation.
    const container = messagesContainerRef.current;
    if (!container || !user?.uid || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(async entries => {
      if (document.visibilityState !== 'visible') return;
      // IntersectionObserver fires an initial callback for any intersecting
      // element, even below the configured threshold. Enforce the 60% rule
      // explicitly so partially visible messages are not marked as read.
      const visibleIds = getVisibleChatMessageIds(entries, 0.6);
      const visibleUnread = messages.filter(message =>
        visibleIds.includes(String(message._id)) &&
        message.receiverUid === user.uid &&
        message.status !== 'read' &&
        !readReceiptInFlightRef.current.has(String(message._id))
      );
      for (let index = 0; index < visibleUnread.length; index += 100) {
        const batch = visibleUnread.slice(index, index + 100);
        const ids = batch.map(message => String(message._id));
        ids.forEach(id => readReceiptInFlightRef.current.add(id));
        try {
          await apiClient.post('/chat/read', { roomId: chatId, messageIds: ids });
          batch.forEach(message => addMessageRef.current({ ...message, status: 'read' }));
        } catch (readError) {
          ids.forEach(id => readReceiptInFlightRef.current.delete(id));
          logger.debug('Message read receipt deferred:', readError?.status || 'network error');
        }
      }
    }, { root: container, threshold: 0.6 });

    container.querySelectorAll('[data-chat-message-id]').forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [chatId, user?.uid, messages]);

  useEffect(() => {
    let cancelled = false;
    let requestInFlight = false;
    const refreshCallHistory = async () => {
      if (cancelled || requestInFlight) return;
      requestInFlight = true;
      try {
        const result = await apiClient.get('/calls/history', { params: { roomId: chatId } });
        if (!cancelled && Array.isArray(result?.calls)) setCallHistory(result.calls);
      } catch (historyError) {
        if (!cancelled) logger.debug('Call history refresh unavailable:', historyError?.status || 'network error');
      } finally {
        requestInFlight = false;
      }
    };
    const pollCallHistory = () => {
      if (document.visibilityState === 'visible') refreshCallHistory();
    };
    if (user?.uid && chatId) {
      refreshCallHistory();
      const interval = window.setInterval(pollCallHistory, 15000);
      document.addEventListener('visibilitychange', pollCallHistory);
      return () => {
        cancelled = true;
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', pollCallHistory);
      };
    }
    return () => { cancelled = true; };
  }, [chatId, user?.uid]);

  useEffect(() => () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (chatId) apiClient.post('/chat/typing', { roomId: chatId, isTyping: false }).catch(() => {});
  }, [chatId]);

  useEffect(() => {
    let cancelled = false;
    let requestInFlight = false;
    const refreshTyping = async () => {
      if (cancelled || requestInFlight) return;
      requestInFlight = true;
      try {
        const result = await apiClient.get('/chat/typing', { params: { roomId: chatId } });
        if (!cancelled) setIsTyping(Boolean(result?.isTyping));
      } catch (_) {
        if (!cancelled) setIsTyping(false);
      } finally {
        requestInFlight = false;
      }
    };
    const pollTyping = () => {
      if (document.visibilityState === 'visible') refreshTyping();
    };
    if (user?.uid && chatId) {
      refreshTyping();
      const interval = window.setInterval(pollTyping, 1200);
      document.addEventListener('visibilitychange', pollTyping);
      return () => {
        cancelled = true;
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', pollTyping);
      };
    }
    return () => { cancelled = true; };
  }, [chatId, user?.uid]);

  useEffect(() => {
    // Socket.IO Room Joining
    if (socket) {
      socket.emit('joinRoom', chatId, (ack) => {
        if (ack && ack.status === 'ok') {
          setIsRoomJoined(true);
          logger.info(`Successfully joined room via Socket.IO: ${chatId}`);
        }
      });
    }

    // Firebase Realtime Database Listener for Realtime Serverless Messages
    let chatRef;
    if (process.env.REACT_APP_ENABLE_FIREBASE_REALTIME_SYNC === 'true' && database && chatId) {
      try {
        chatRef = ref(database, `chats/${chatId}`);
        onChildAdded(chatRef, (snapshot) => {
          const msg = snapshot.val();
          if (msg) {
            addMessage({
              _id: msg._id || snapshot.key,
              roomId: chatId,
              senderUid: msg.senderUid,
              receiverUid: msg.receiverUid,
              messageContent: msg.messageContent,
              timestamp: msg.timestamp,
            });
          }
        });
        setIsRoomJoined(true);
      } catch (fbErr) {
        logger.warn('Firebase RTDB listener error:', fbErr);
      }
    }

    const fetchOtherUser = async () => {
      try {
        const data = await apiClient.get(`/users/${otherUserUid}`);
        setOtherUser(data);
        setIsRoomJoined(true); // Allow sending once recipient is known
      } catch (err) {
        setError('Could not load user details.');
      }
    };

    if (otherUserUid) {
      fetchOtherUser();
    }

    return () => {
      if (chatRef) {
        try {
          off(chatRef);
        } catch (e) {
          // Ignored
        }
      }
    };
  }, [socket, chatId, user?.uid, otherUserUid]);

  const loadOlderMessages = async () => {
    if (isLoadingOlder || !hasOlderMessages) return;
    const cursor = olderHistoryCursorRef.current;
    if (!cursor) {
      setHasOlderMessages(false);
      return;
    }
    const container = messagesContainerRef.current;
    if (container) {
      pendingScrollRestoreRef.current = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
      };
    }
    setIsLoadingOlder(true);
    try {
      const result = await apiClient.get(
        `/chat/history/${encodeURIComponent(chatId)}?limit=50&before=${encodeURIComponent(cursor.timestamp)}&beforeId=${encodeURIComponent(cursor.id)}`
      );
      const olderMessages = Array.isArray(result?.messages) ? result.messages : [];
      olderMessages.forEach(message => addMessageRef.current(message));
      const oldest = olderMessages.find(message =>
        message._id && /^[a-fA-F0-9]{24}$/.test(String(message._id))
      );
      olderHistoryCursorRef.current = oldest ? {
        timestamp: new Date(oldest.timestamp).toISOString(),
        id: String(oldest._id),
      } : null;
      setHasOlderMessages(olderMessages.length === 50 && Boolean(olderHistoryCursorRef.current));
      if (!olderMessages.length) pendingScrollRestoreRef.current = null;
    } catch (historyError) {
      pendingScrollRestoreRef.current = null;
      setError('Older messages could not be loaded. Please try again.');
    } finally {
      setIsLoadingOlder(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;

    const receiverUid = otherUser?.uid;
    if (!receiverUid) {
      setError("Could not determine the recipient of the message.");
      return;
    }

    const messageContent = input.trim();
    // Keep the same idempotency key when the user retries the same text after
    // a timeout or lost response. A changed draft receives a fresh key.
    const pending = pendingMessageRef.current;
    const clientMessageId = pending?.messageContent === messageContent
      ? pending.clientMessageId
      : (window.crypto?.randomUUID?.() ||
        `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, character => {
          const random = Math.floor(Math.random() * 16);
          return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
        }));
    pendingMessageRef.current = { clientMessageId, messageContent };
    const tempId = `temp_${clientMessageId}`;
    const message = {
      _id: tempId,
      clientMessageId,
      roomId: chatId,
      senderUid: user.uid,
      receiverUid: receiverUid,
      messageContent: messageContent,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    // Stop the live typing signal as the message is submitted.
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = null;
    typingLastSentAtRef.current = 0;
    if (socket?.connected) socket.emit('stopTyping', { roomId: chatId, receiverUid });
    apiClient.post('/chat/typing', { roomId: chatId, isTyping: false }).catch(() => {});

    // Optimistically update UI
    addMessage(message);
    setInput('');
    setError(null);
    setIsSending(true);

    // MongoDB through the Worker API is the authoritative message store.
    // Socket.IO is disabled in the Cloudflare serverless deployment and must
    // never be treated as proof that a message was persisted.
    try {
      const savedMessage = await apiClient.post('/chat/send', {
        roomId: chatId,
        messageContent,
        receiverUid,
        clientMessageId,
      });
      if (!savedMessage || !savedMessage._id) {
        throw new Error('The chat service did not confirm message persistence.');
      }
      pendingMessageRef.current = null;
      addMessage({
        ...savedMessage,
        _id: String(savedMessage._id),
        roomId: savedMessage.roomId || chatId,
        status: savedMessage.status || 'sent',
      });
    } catch (sendErr) {
      logger.error('Failed to persist chat message:', sendErr);
      removeMessage(chatId, tempId);
      setInput(messageContent);
      setError('Message could not be sent. Your text has been restored. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    if (!otherUser?.uid) return;

    const wasTyping = Boolean(typingTimeoutRef.current);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (value.trim()) {
      const now = Date.now();
      if (!wasTyping || now - typingLastSentAtRef.current > 2500) {
        if (socket?.connected) socket.emit('typing', { roomId: chatId, receiverUid: otherUser.uid });
        apiClient.post('/chat/typing', { roomId: chatId, isTyping: true }).catch(() => {});
        typingLastSentAtRef.current = now;
      }
      typingTimeoutRef.current = setTimeout(() => {
        if (socket?.connected) socket.emit('stopTyping', { roomId: chatId, receiverUid: otherUser.uid });
        apiClient.post('/chat/typing', { roomId: chatId, isTyping: false }).catch(() => {});
        typingLastSentAtRef.current = 0;
        typingTimeoutRef.current = null;
      }, 1800);
    } else {
      if (socket?.connected) socket.emit('stopTyping', { roomId: chatId, receiverUid: otherUser.uid });
      apiClient.post('/chat/typing', { roomId: chatId, isTyping: false }).catch(() => {});
      typingLastSentAtRef.current = 0;
      typingTimeoutRef.current = null;
    }
  };

  if (!user) {
    return <p className="p-4">Please log in to chat.</p>;
  }

  return (
    <div className="chat-shell chat-whatsapp-shell flex flex-col">
      <header className="chat-header chat-whatsapp-header flex flex-shrink-0 items-center justify-between gap-3 px-3 sm:px-5">
        <Link to="/conversations" aria-label="Back to conversations" className="chat-back-link flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-lg transition" title="All conversations">
          <span aria-hidden="true">←</span>
        </Link>
        <Link to={otherProfilePath} className="chat-participant-link flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left" aria-label={`View ${otherUser?.name || 'chat participant'}'s profile`}>
          <div aria-hidden="true" className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-pink-100 text-base font-bold text-pink-700 ring-1 ring-pink-100">
            {otherUser?.photos?.[0]?.url ? <img src={resolvePhotoUrl(otherUser.photos[0].url)} alt="" className="h-full w-full object-cover" onError={(event) => { const image = event.currentTarget; const fallback = resolveWorkerPhotoUrl(otherUser.photos[0].url); if (fallback && image.src !== fallback) { image.src = fallback; return; } image.src = PLACEHOLDER_IMAGE; }} /> : (otherUser?.name || '?').trim().charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-gray-900">{otherUser?.name || 'Conversation'}</h2>
            <p className="chat-participant-status text-xs">{otherUser ? 'Your conversation' : 'Profile details loading'}</p>
          </div>
        </Link>
        <div id={`chat-call-actions-${chatId}`} className="chat-header-actions flex flex-shrink-0 items-center gap-1.5" aria-label="Voice and video call actions" />
      </header>
      {error && <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" role="alert">{error}</div>}
      {otherUser && user && <ChatCallPanel roomId={chatId} currentUser={user} otherUser={otherUser} />}
      <main ref={messagesContainerRef} className="chat-messages chat-wallpaper flex-grow overflow-y-auto px-3 py-5 sm:px-6">
        <div className="chat-message-stack mx-auto flex max-w-3xl flex-col gap-3">
          {hasOlderMessages && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                onClick={loadOlderMessages}
                disabled={isLoadingOlder}
                className="rounded-full border border-gray-200 bg-white/90 px-4 py-2 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-white disabled:cursor-wait disabled:opacity-60"
              >
                {isLoadingOlder ? 'Loading older messages…' : 'Load older messages'}
              </button>
            </div>
          )}
          {[...messages, ...callHistory.map(call => ({
            _id: `call-${call._id}`,
            messageType: 'call',
            call,
            senderUid: call.callerUid,
            receiverUid: call.receiverUid,
            timestamp: call.createdAt || call.updatedAt,
          }))].sort((a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime()).map((msg) => (
            <div
              key={msg._id}
              data-chat-message-id={msg.messageType === 'call' ? undefined : String(msg._id)}
            >
              <ChatMessage
                message={msg}
                currentUser={user}
                otherUser={otherUser}
              />
            </div>
          ))}
        </div>
        <div ref={messagesEndRef} />
        {isTyping && (
          <div className="chat-typing-indicator flex items-center gap-1.5" role="status" aria-label="The other person is typing">
            <span /><span /><span />
          </div>
        )}
      </main>
      <footer className="chat-composer chat-whatsapp-composer flex-shrink-0">
        <form onSubmit={sendMessage} className="chat-composer-form mx-auto flex w-full max-w-3xl items-center gap-2 sm:gap-3">
          <span className="chat-composer-mark" aria-hidden="true">♡</span>
          <input
            value={input}
            onChange={handleInputChange}
            className="chat-message-input flex-grow"
            placeholder={!isRoomJoined ? "Connecting to chat…" : isSending ? "Sending message…" : "Write a message…"}
            aria-label="Message"
            autoComplete="off"
            disabled={!isRoomJoined || isSending}
          />
          <button
            type="submit"
            className="chat-send-button flex-shrink-0 disabled:cursor-not-allowed"
            disabled={!isRoomJoined || !input.trim() || isSending}
            aria-label={isSending ? 'Sending message' : 'Send message'}
            title={isSending ? 'Sending…' : 'Send message'}
          >
            {isSending ? <span className="chat-send-spinner" aria-hidden="true" /> : <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m21 3-7.2 18-3.7-7.1L3 10.2 21 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M10.1 13.9 15 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
          </button>
        </form>
      </footer>
    </div>
  );
}

export default Chat;